import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Leaf, Loader2, KeyRound, Eye, EyeOff, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PractitionerResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate('/practitioner'), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'This reset link is invalid or has expired.');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-6" data-testid="practitioner-reset">
      <div className="bg-white rounded-3xl shadow-soft border border-stone-200 p-8 w-full max-w-sm">
        <div className="flex items-center gap-2 text-emerald-800 font-bold mb-1"><Leaf className="w-5 h-5" /> Practitioner Portal</div>
        {done ? (
          <div className="text-center py-6" data-testid="pp-reset-done">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
            <p className="text-sm text-stone-600">Password updated. Redirecting you to sign in…</p>
          </div>
        ) : !token ? (
          <p className="text-sm text-stone-500 mt-4" data-testid="pp-reset-notoken">
            This reset link is missing or invalid. Please request a new one from the{' '}
            <Link to="/practitioner" className="text-emerald-700 font-semibold underline">sign-in page</Link>.
          </p>
        ) : (
          <form onSubmit={submit}>
            <p className="flex items-center gap-2 text-sm text-stone-500 mb-6"><KeyRound className="w-4 h-4" /> Choose a new password</p>
            <div className="relative mb-4">
              <input type={showPw ? 'text' : 'password'} required placeholder="New password (min 8 characters)" value={pw} onChange={(e) => setPw(e.target.value)} className="w-full border-2 border-stone-200 rounded-xl px-4 py-3 pr-11 text-sm focus:outline-none focus:border-emerald-500" data-testid="pp-reset-password" />
              <button type="button" onClick={() => setShowPw(v => !v)} aria-label={showPw ? 'Hide password' : 'Show password'} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-emerald-700" data-testid="pp-reset-toggle">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <button type="submit" disabled={busy} className="w-full bg-emerald-800 hover:bg-emerald-900 text-white font-semibold py-3.5 rounded-xl transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="pp-reset-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Set new password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
